import * as React from 'react';

import {
  maybeParseCalendarDate,
  toISODateString,
} from '@rematter/common/dateTime';
import {
  enumBookingStatus,
  enumBrokeredOrderStatus,
  enumSalesOrderStatus,
} from '@rematter/frontend-lib-api-client';
import { ContentSection, cn } from '@rematter/ui-web';
import { Grid, SmartDatePicker, TextField } from '@rematter/ui-web/components';
import { useAtom, useAtomValue } from 'jotai';

import { SelectBrokeredOrder } from '../../select-fields/SelectBrokeredOrder';

import {
  $brokeredLoadCreateForm,
  $brokeredOrderLinkedOrders,
} from '~/modules/brokerage/_state/brokeredLoadCreateForm.state';
import { useTranslations } from '~/modules/brokerage/i18n/useTranslations';
import { SelectBooking } from '~/shared/components/select-fields/SelectBooking';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { SelectSalesOrder } from '~/shared/components/select-fields/SelectSalesOrder';
import { SelectSupplierAccount } from '~/shared/components/select-fields/SelectSupplierAccount';

type BrokeredLoadWizardStepInfoProps = {
  disabled?: boolean;
};

export const BrokeredLoadWizardStepInfo = ({
  disabled,
}: BrokeredLoadWizardStepInfoProps) => {
  const t = useTranslations();

  const [form, updateForm] = useAtom($brokeredLoadCreateForm);
  const linkedOrders = useAtomValue($brokeredOrderLinkedOrders);

  const linkedSoIds = React.useMemo(() => {
    if (!form.brokeredOrderId || !linkedOrders?.data) return undefined;
    return linkedOrders.data.salesOrderIds;
  }, [form.brokeredOrderId, linkedOrders?.data]);

  const handleSupplierChange = (value?: string) => {
    updateForm({
      ...form,
      supplierId: value?.toString(),
      brokeredOrderId: undefined,
      salesOrderId: undefined,
    });
  };

  const handleBrokeredOrderChange = (value?: string) => {
    updateForm({
      ...form,
      brokeredOrderId: value?.toString(),
      salesOrderId: undefined,
    });
  };

  const handleDateChange = (value: string | null) => {
    updateForm({
      ...form,
      expectedShipDate: value ? maybeParseCalendarDate(value) : undefined,
    });
  };

  const handleChangeFieldValue =
    (field: keyof typeof form) => (value: string | undefined) => {
      const fieldValue = value?.toString() || undefined;
      updateForm({
        ...form,
        [field]: fieldValue,
      });
    };

  const bookingValidStatus = React.useMemo(() => [enumBookingStatus.Open], []);
  const brokeredOrderValidStatuses = React.useMemo(
    () => [enumBrokeredOrderStatus.Open],
    [],
  );

  return (
    <ContentSection
      className={cn('@BrokeredLoadWizardStepInfo', '!border-t-0 !pt-0')}
    >
      <Grid cols3 gap4>
        {form.documentNumber != null && (
          <TextField
            label={t.brokerage.loads.create.fields.loadNumber}
            value={form.documentNumber ?? ''}
            onChange={handleChangeFieldValue('documentNumber')}
            placeholder={t.common.placeholders.generateNumber}
            isDisabled={disabled}
          />
        )}
        <SmartDatePicker
          autoFocus
          value={form.expectedShipDate}
          label={t.brokerage.loads.create.fields.expectedShipDate}
          onChange={(date) =>
            handleDateChange(date ? toISODateString(date) : null)
          }
          isRequired
          isDisabled={disabled}
        />
        <SelectFacility
          value={form.facilityId}
          label={t.brokerage.loads.create.fields.facility}
          onChange={handleChangeFieldValue('facilityId')}
          isRequired
          isDisabled={disabled}
          autoSelectFirstOption
        />
      </Grid>
      <Grid cols2 gap4 className="mt-4">
        <SelectSupplierAccount
          value={form.supplierId}
          label={t.brokerage.loads.create.fields.supplier}
          onChange={handleSupplierChange}
          isRequired
          isDisabled={disabled}
        />
        <SelectBrokeredOrder
          value={form.brokeredOrderId}
          label={t.brokerage.loads.create.fields.brokeredOrder}
          onChange={handleBrokeredOrderChange}
          supplierIds={form.supplierId ? [form.supplierId] : undefined}
          statuses={brokeredOrderValidStatuses}
          isDisabled={disabled || !form.supplierId}
        />
        <SelectSalesOrder
          value={form.salesOrderId}
          label={t.brokerage.loads.create.fields.salesOrder}
          onChange={handleChangeFieldValue('salesOrderId')}
          statuses={[enumSalesOrderStatus.Open]}
          ids={linkedSoIds}
          isDisabled={disabled}
        />
        <SelectBooking
          value={form.bookingId}
          label={t.brokerage.loads.create.fields.booking}
          statuses={bookingValidStatus}
          onChange={handleChangeFieldValue('bookingId')}
          isDisabled={disabled}
        />
      </Grid>
    </ContentSection>
  );
};
